import {
  Box,
  Button,
  Center,
  Flex,
  HStack,
  IconButton,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import type { ProjectTimeBillingListItemFragment } from "@src/__generated__/graphql";
import { TIME_BILLING_URL_PARAMS } from "@src/components/modules/projects/time-billing/constants";
import {
  type BodyColumnMeta,
  DataTable,
  formatDate,
  LoadingOverlay,
  ModalConfirm,
} from "@src/components/ui-kit";
import { Icon } from "@src/components/ui-kit/Icon";
import { NavigationProject } from "@src/components/widgets/AppSubNav";
import { can } from "@src/utils/components/permissions";
import { currency } from "@src/utils/formatters";
import { useStore } from "@src/utils/hooks";
import { useWarnBeforeUnload } from "@src/utils/hooks/warn-before-unload";
import { getQueryParamOptional } from "@src/utils/routing";
import { observer, useLocalObservable } from "mobx-react-lite";
import { useRouter } from "next/router";
import { type ReactNode, useEffect } from "react";
import { ProjectDetailLoading } from "../common/project-detail-loading";
import { CreateModal } from "./components/create-modal";
import { DistributionsModal } from "./components/distributions-modal";
import { ExpandedRow } from "./components/expanded-row";
import { SourcesEditModal } from "./components/sources-edit-modal";
import { TimeBillingStore } from "./store";

type Props = {
  readonly id: string;
};

type TimeBillingColumn = {
  key: string;
  header: string | (() => string);
  render?: (row: ProjectTimeBillingListItemFragment) => ReactNode;
  align?: BodyColumnMeta["align"];
};

export const ProjectTimeBilling = observer(function ProjectTimeBilling({
  id,
}: Props) {
  const appStore = useStore();
  const router = useRouter();
  const { store } = useLocalObservable(() => ({
    store: new TimeBillingStore(id, appStore),
  }));
  const detailId = getQueryParamOptional(
    TIME_BILLING_URL_PARAMS.EXPAND,
    router.query,
  );

  useEffect(() => {
    if (!router.isReady) return;
    store.initializeFromUrlParams(detailId);
  }, [router.isReady, detailId]);

  useWarnBeforeUnload(store.hasUnsavedChanges);

  const columns: TimeBillingColumn[] = [
    {
      key: "expand",
      header: "",
      render: (row) => (
        <IconButton
          aria-label={t`Toggle expand`}
          icon={
            <Icon
              name={
                store.expandedItemIds.has(row.id)
                  ? "chevron-down"
                  : "chevron-right"
              }
            />
          }
          onClick={() => store.toggleExpandedItem(row.id)}
          size="sm"
          variant="ghost"
        />
      ),
    },
    {
      key: "date_range",
      header: () => t`Date Range`,
      render: (row) => {
        const title = row.title?.trim();

        return (
          <VStack alignItems="start" spacing="0">
            <Text fontSize="sm">
              {formatDate(new Date(row.date_from))}
              &nbsp;-&nbsp;
              {formatDate(new Date(row.date_to))}
            </Text>
            {title && (
              <Text color="gray.500" fontSize="xs">
                {title}
              </Text>
            )}
          </VStack>
        );
      },
    },
    {
      key: "final_total_amount",
      header: () => t`Total Amount`,
      align: "right",
      render: (row) => (
        <Text pr="2" fontSize="sm" fontWeight="medium">
          {currency.formatByWorkspace(Number(row.final_total_amount))}
        </Text>
      ),
    },
    {
      key: "type",
      header: () => t`Type`,
      render: (row) => (
        <Text fontSize="sm">{row.type === "AUTO" ? t`Auto` : t`Manual`}</Text>
      ),
    },
    {
      key: "is_locked",
      header: () => t`Status`,
      render: (row) => (
        <Tooltip
          hasArrow
          isDisabled={!row.is_locked}
          label={
            row.is_locked && row.lockedByUser && row.locked_at
              ? t`Locked by ${row.lockedByUser.full_name} on ${formatDate(new Date(row.locked_at))}`
              : undefined
          }
          placement="top"
        >
          <HStack spacing="1">
            <Icon
              name={row.is_locked ? "lock-01" : "lock-unlocked-01"}
              variant="line"
            />
            <Text fontSize="sm">{row.is_locked ? t`Locked` : t`Unlocked`}</Text>
          </HStack>
        </Tooltip>
      ),
    },
    {
      key: "created_at",
      header: () => t`Created`,
      render: (row) => (
        <VStack alignItems="start" spacing="0">
          <Text fontSize="sm">{formatDate(new Date(row.created_at))}</Text>
          <Text color="gray.500" fontSize="xs">
            {row.createdByUser.full_name}
          </Text>
        </VStack>
      ),
    },
    {
      key: "actions",
      header: () => t`Actions`,
      render: (row) => (
        <HStack justify="end">
          {can("project_billingTime_lock") &&
            (row.is_locked ? (
              <Tooltip hasArrow label={t`Unlock`} placement="top">
                <IconButton
                  aria-label={t`Unlock`}
                  icon={<Icon name="lock-unlocked-01" />}
                  isLoading={store.isItemLoading(row.id)}
                  onClick={() => store.unlock(row.id)}
                  size="sm"
                  variant="ghost"
                />
              </Tooltip>
            ) : (
              <Tooltip hasArrow label={t`Lock`} placement="top">
                <IconButton
                  aria-label={t`Lock`}
                  icon={<Icon name="lock-01" />}
                  isLoading={store.isItemLoading(row.id)}
                  onClick={() => store.lock(row.id)}
                  size="sm"
                  variant="ghost"
                />
              </Tooltip>
            ))}
          <Tooltip
            hasArrow
            label={
              row.is_locked
                ? t`This entry is locked and cannot be deleted`
                : t`Delete`
            }
            placement="top"
          >
            <IconButton
              aria-label={t`Delete`}
              colorScheme="red"
              icon={<Icon name="trash-03" />}
              isDisabled={row.is_locked}
              onClick={() => store.openDeleteConfirm(row.id)}
              size="sm"
              variant="ghost"
            />
          </Tooltip>
        </HStack>
      ),
    },
  ];

  if (store.isListingLoading) {
    return <ProjectDetailLoading id={id} />;
  }

  return (
    <>
      <DataTable>
        <Flex
          alignItems="center"
          borderBottom="1px"
          borderBottomColor="gray.200"
        >
          <Box flex="1" minW={0} pl="4">
            <NavigationProject id={id} />
          </Box>
          <HStack flexShrink={0} spacing="4" pr="4">
            <Text color="gray.500" whiteSpace="nowrap">
              {store.total} {store.total === 1 ? t`record` : t`records`}
            </Text>
            <Button
              leftIcon={<Icon name="plus" />}
              onClick={store.createModalStore.disclosureState.onOpen}
            >
              <Trans>Create</Trans>
            </Button>
          </HStack>
        </Flex>

        <LoadingOverlay
          isLoading={store.isListingLoading}
          position="absolute"
          minW="50dvh"
        >
          {store.items.length === 0 && !store.isListingLoading ? (
            <Center py="16">
              <VStack spacing="4">
                <Text color="gray.500">
                  <Trans>No time billing records yet.</Trans>
                </Text>
                <Button
                  leftIcon={<Icon name="plus" />}
                  onClick={store.createModalStore.disclosureState.onOpen}
                >
                  <Trans>Create first record</Trans>
                </Button>
              </VStack>
            </Center>
          ) : (
            <Box>
              {store.items.map((item) => (
                <Box key={item.id}>
                  <Flex
                    align="center"
                    px="4"
                    py="2"
                    borderColor="gray.200"
                    borderBottomWidth="1px"
                    _hover={{ bg: "gray.50" }}
                  >
                    {columns.map((col) => (
                      <Box
                        key={col.key}
                        flex={
                          col.key === "expand" || col.key === "actions"
                            ? "0 0 auto"
                            : 1
                        }
                        minW={0}
                        textAlign={col.align}
                      >
                        {col.render?.(item)}
                      </Box>
                    ))}
                  </Flex>

                  {store.expandedItemIds.has(item.id) && (
                    <Box
                      p="4"
                      bg="gray.50"
                      borderColor="gray.200"
                      borderBottomWidth="1px"
                    >
                      <ExpandedRow item={item} store={store} />
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </LoadingOverlay>
      </DataTable>

      <CreateModal store={store.createModalStore} />

      <SourcesEditModal store={store.sourcesEditModalStore} />

      <DistributionsModal store={store.distributionsModalStore} />

      <ModalConfirm
        isOpen={store.deleteConfirmState.isOpen}
        onClose={store.deleteConfirmState.onClose}
        onConfirm={() => {
          if (store.deleteConfirmState.additionalData?.id) {
            store.delete(store.deleteConfirmState.additionalData.id);
          }
        }}
        heading={t`Delete Time Billing`}
        message={
          <Trans>
            Are you sure you want to delete this time billing record? This
            action cannot be undone.
          </Trans>
        }
        isLoading={store.isDeleteLoading}
      />
    </>
  );
});
